import { useAtomValue, useSetAtom } from "jotai";
import { useRef, useEffect } from "react";
import { Drawer } from "@zapier/product-nav";
import {
  sideBarSelectedAtom,
  sideBarWidthAtom,
  SUMMARY,
  togglesAtomResettable,
} from "@zapier/canvas-state";
import { PRODUCT_NAV_WIDTH } from "@zapier/canvas-constants";
import { Composer } from "@zapier/agent-system";

import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import { SummaryPanel } from "../SummaryPanel";
import DevTools from "../DevTools/DevTools";
import styles from "./SidebarDrawer.module.css";
import { CopilotChatPanel } from "@zapier/ai-toolkit/client";

type Props = {
  handleOnClose: () => void;
};

export default function SidebarDrawer({ handleOnClose }: Props) {
  const toggles = useAtomValue(togglesAtomResettable);
  const sideBarSelected = useAtomValue(sideBarSelectedAtom);
  const setSideBarWidth = useSetAtom(sideBarWidthAtom);
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const findAndMeasureDrawer = () => {
      const drawerElement = document.querySelector(
        '[data-testid="BuilderFrameDrawer"] > section',
      );

      if (drawerElement) {
        const drawerBounds = drawerElement.getBoundingClientRect();
        // Add product nav width to the drawer width
        setSideBarWidth(PRODUCT_NAV_WIDTH + drawerBounds.width);
      }
    };

    if (sideBarSelected) {
      // Add a small delay to ensure DOM is updated
      const timeoutId = setTimeout(() => {
        findAndMeasureDrawer();
      }, 10);

      return () => clearTimeout(timeoutId);
    } else {
      // Even when sidebar is closed, we need to account for the product nav width
      setSideBarWidth(PRODUCT_NAV_WIDTH);
    }
  }, [sideBarSelected, setSideBarWidth]);

  const SIDEBAR_CONTENT = {
    [SUMMARY]: {
      title: capitalizeFirstLetter(SUMMARY),
      component: <SummaryPanel />,
    },
    devTools: {
      title: "🔒 DevTools - staff only (⌘D)",
      component: <DevTools />,
    },
    composer: {
      title: "Copilot",
      component: toggles.aiCobuildCopilot ? (
        <div className={styles.copilotChatPanelContainer}>
          <CopilotChatPanel placeholder="Ask me anything about your canvas..." />
        </div>
      ) : (
        <Composer
          showDevTools={toggles.aiComposerDevTools}
          showDataPills={toggles.aiComposerDataPills}
        />
      ),
    },
  };

  const selectedSidebar =
    sideBarSelected &&
    SIDEBAR_CONTENT[sideBarSelected as keyof typeof SIDEBAR_CONTENT]
      ? SIDEBAR_CONTENT[sideBarSelected as keyof typeof SIDEBAR_CONTENT]
      : {
          title: "",
          component: null,
        };

  // With the div wrapping drawer we have to add display: contents
  // The drawer should cover the sidebar icons in draft mode when the composer is open

  return (
    <div ref={drawerRef} className={styles.drawerContainer}>
      <Drawer
        onClose={handleOnClose}
        title={selectedSidebar.title as string}
        isOpen={Boolean(sideBarSelected)}
        isFixed={false}
        hasSidePadding={sideBarSelected !== SUMMARY}
      >
        {selectedSidebar.component}
      </Drawer>
    </div>
  );
}
